<script lang="ts">
    import type {RecordRow} from 'src/features/record-grid/lib/types';
    import type {ViewableFile} from 'typings/portaro.be.types';
    import type {TextValueEditorOptions} from 'shared/value-editors/internal/editors/text/types';
    import {getIdFromGridFieldValue, getTextFromGridFieldValue} from '../../../../../sutor-utils';
    import {getSutorReportsOptimisationContext} from '../lib/sutor-reports-optimisation-context';
    import {hasRecordReference} from 'src/features/record-grid/lib/types-utils';
    import {getFirstFieldByFieldTypeIdRecursive} from 'src/features/record-grid/lib/grid-fields';
    import {onMount} from 'svelte';
    import {exists} from 'shared/utils/custom-utils';
    import {SUTOR_REPORTS_APPROVING_SETTINGS, SUTOR_REPORTS_OPTIMISATION_SETTINGS} from '../../../../../sutor-constants';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import KpValueEditor from 'shared/value-editors/kp-value-editor/KpValueEditor.svelte';
    import KpChipTag from 'shared/ui-widgets/chip/KpChipTag.svelte';
    import KpSingularFileViewer from '../../../../../../media-viewer/KpSingularFileViewer.svelte';
    import KpUserAvatar from 'shared/components/kp-user-avatar/KpUserAvatar.svelte';

    export let reportRecord: RecordRow;

    const context = getSutorReportsOptimisationContext();

    const stateField = getFirstFieldByFieldTypeIdRecursive(reportRecord, SUTOR_REPORTS_APPROVING_SETTINGS.stateFieldId);
    const textNoteField = getFirstFieldByFieldTypeIdRecursive(reportRecord, SUTOR_REPORTS_APPROVING_SETTINGS.textNoteFieldId);
    const dateField = getFirstFieldByFieldTypeIdRecursive(reportRecord, SUTOR_REPORTS_APPROVING_SETTINGS.dateFieldId);
    const typeField = getFirstFieldByFieldTypeIdRecursive(reportRecord, SUTOR_REPORTS_APPROVING_SETTINGS.typeFieldId);
    const referenceField = getFirstFieldByFieldTypeIdRecursive(reportRecord, SUTOR_REPORTS_APPROVING_SETTINGS.referenceFieldId);
    const projectItemField = getFirstFieldByFieldTypeIdRecursive(reportRecord, SUTOR_REPORTS_APPROVING_SETTINGS.projectItemFieldId);
    const workerField = getFirstFieldByFieldTypeIdRecursive(reportRecord, SUTOR_REPORTS_APPROVING_SETTINGS.workWorkerFieldId);
    const projectField = getFirstFieldByFieldTypeIdRecursive(reportRecord, SUTOR_REPORTS_OPTIMISATION_SETTINGS.projectFieldId);
    const forOptimisationField = getFirstFieldByFieldTypeIdRecursive(reportRecord, SUTOR_REPORTS_OPTIMISATION_SETTINGS.forOptimisationFieldId);

    const isForOptimisation = exists(forOptimisationField) && typeof forOptimisationField === 'boolean' && forOptimisationField === true;

    let saving = false;
    let referencePdfFile: ViewableFile | null = null;
    let textNoteModel = getTextFromGridFieldValue(textNoteField, '');

    onMount(async () => {
        referencePdfFile = await context.service.getReportReferencePdfFile(referenceField);
    });

    function getAceEditorOptions(): TextValueEditorOptions {
        return {
            type: 'rich-textarea',
            aceOptions: {
                showPrintMargin: false
            }
        };
    }

    const handleUseForOptimisation = async () => {
        saving = true;
        const success = await context.service.useForOptimisation(context.projectRecord, reportRecord);

        if (success) {
            context.addUsedReportForOptimisation(reportRecord.id);
        }

        saving = false;
    };
</script>

<div class="report-optimisation-user-detail">
    <div class="heading-container">
        <div class="user-container">
            {#if exists(workerField)}
                {#if hasRecordReference(workerField)}
                    <KpUserAvatar user="{null}"
                                  sizePx="{48}"
                                  userId="{workerField.recordReference.id}"
                                  userName="{getTextFromGridFieldValue(workerField)}"/>
                {/if}

                <span class="username">{getTextFromGridFieldValue(workerField)}</span>
            {:else}
                LINK NEEXISTUJE
            {/if}
        </div>

        <div class="state-buttons-container">
            <KpButton buttonStyle="success-new" isDisabled="{saving}" on:click={handleUseForOptimisation}>
                <IconedContent icon="arrow-small-left">Použít k optimalizaci</IconedContent>
            </KpButton>
        </div>
    </div>

    <div class="details-container">
        <div class="detail-column">
            <span class="info-label">Datum:</span>
            <span class="info-value">{getTextFromGridFieldValue(dateField)}</span>
        </div>

        <div class="detail-column">
            <span class="info-label">Zakázka:</span>
            <span class="info-value">{getTextFromGridFieldValue(projectField)}</span>
        </div>

        <div class="detail-column">
            <span class="info-label">Položka zakázky:</span>
            <span class="info-value">{getTextFromGridFieldValue(projectItemField)}</span>
        </div>

        <div class="detail-column">
            <span class="info-label">Typ:</span>
            <span class="info-value">{getTextFromGridFieldValue(typeField)}</span>
        </div>

        <div class="detail-column">
            <span class="info-label label-bottom-padded">Vhodný k optimalizaci:</span>
            <KpChipTag chipSize="sm" chipStyle="{isForOptimisation ? 'success-new' : 'danger-new'}">
                <IconedContent icon="{isForOptimisation ? 'check-circle' : 'cross-circle'}">
                    {isForOptimisation ? 'Ano' : 'Ne'}
                </IconedContent>
            </KpChipTag>
        </div>

        <div class="detail-column">
            <span class="info-label label-bottom-padded">Stav:</span>
            <KpChipTag chipSize="sm"
                       chipStyle="{SUTOR_REPORTS_APPROVING_SETTINGS.stateChipStyles[getIdFromGridFieldValue(stateField)] ?? 'default'}">

                {getTextFromGridFieldValue(stateField)}
            </KpChipTag>
        </div>
    </div>

    {#if exists(referencePdfFile)}
        <div class="reference-pdf-container column-container">
            <span class="text-muted">Průvodka:</span>
            <div class="verbis-viewer-container">
                <KpSingularFileViewer file="{referencePdfFile}"/>
            </div>
        </div>
    {/if}

    <div class="text-note-container column-container">
        <span class="text-muted">Poznámka:</span>

        <KpValueEditor type="text"
                       isDisabled="{true}"
                       options="{getAceEditorOptions()}"
                       bind:model="{textNoteModel}"/>

        <small class="text-muted">Poznámka není během optimalizace editovatelná.</small>
    </div>
</div>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro.themes.less";

    .report-optimisation-user-detail {
        display: flex;
        flex-direction: column;
        gap: @spacing-xxl;

        .heading-container {
            display: flex;
            align-items: center;
            justify-content: space-between;

            .user-container {
                display: flex;
                align-items: center;
                gap: @spacing-ml;

                .username {
                    font-size: @font-size-xl;
                }
            }

            .state-buttons-container {
                display: flex;
                align-items: center;
                gap: @spacing-s;
            }
        }

        .details-container {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            column-gap: @spacing-xxl;
            row-gap: @spacing-ml;

            .detail-column {
                display: flex;
                flex-direction: column;
                align-items: start;

                .info-value {
                    font-weight: 500;
                    font-size: @font-size-xxl;
                }

                .info-label {
                    color: @themed-text-muted;

                    &.label-bottom-padded {
                        margin-bottom: @spacing-xs;
                    }
                }
            }
        }

        .reference-pdf-container .verbis-viewer-container {
            position: relative;
            height: 500px;
            border-top: 1px solid @themed-border-default;
            border-bottom: 1px solid @themed-border-default;
            margin-left: calc(@spacing-xl * -1);
            margin-right: calc(@spacing-xxl * -1);
            width: calc(@spacing-xl + 100% + @spacing-xxl);
        }

        .column-container {
            display: flex;
            flex-direction: column;
            gap: @spacing-m;
        }
    }

    :global {
        .report-optimisation-user-detail .ace-editor-wrapper {
            .ace-editor {
                height: 125px;
                min-height: 125px;
            }
        }
    }
</style>